import { h, clear } from './dom.js'
import { on } from '../lib/bus.js'
import { state, restoreProject, hasStamps } from '../lib/state.js'
import { buildSnapshot } from '../lib/storage.js'
import {
  buildCsv,
  buildSegments,
  exportGeometry,
  buildFilterScript,
  buildRenderScripts,
  buildKitReadme,
  parseProjectJson,
  buildZip,
  segName,
} from '../lib/exportKit.js'
import { splitGeometry, buildSplitFilterScript } from '../lib/split.js'
import { renderMovie, pickRecorderMime } from '../lib/render.js'
import { loadPieceImages, renderBoardCanvas, canvasToPngBlob } from '../lib/boardImage.js'
import { BOARD_THEMES, PIECE_SETS, CM_ASSETS_URL } from '../config.js'
import { toast } from './toast.js'
import { confirmDialog } from './dialog.js'

export function mountExportPanel(parent) {
  const status = h('p.hint')
  const jsonBtn = h('button.btn', { type: 'button', onClick: exportJson }, 'Project JSON')
  const csvBtn = h('button.btn', { type: 'button', onClick: exportCsv }, 'Timestamps CSV')
  const movieBtn = h('button.btn.primary', { type: 'button', onClick: exportMovie }, '⬇ Video (MP4)')
  const kitBtn = h('button.btn', { type: 'button', onClick: exportKit }, 'Render kit (ZIP)')
  const importBtn = h('button.btn.ghost', { type: 'button', onClick: () => importInput.click() }, 'Import project JSON')
  const importInput = h('input', {
    type: 'file',
    accept: 'application/json,.json',
    hidden: true,
    onChange: async (e) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (file) await importProject(file)
    },
  })

  const panel = h(
    'section.card',
    {},
    h('h2.card-title', {}, 'Export'),
    status,
    h('div.btn-row', {}, movieBtn, kitBtn, jsonBtn, csvBtn),
    h('div.btn-row', {}, importBtn),
    h(
      'p.hint',
      {},
      'Video renders the finished clip right here in the browser, in real time — a 45s clip takes about 45s; keep this tab visible while it runs. For long footage or frame-exact output, the render kit + local ffmpeg produces the same result.',
    ),
    importInput,
  )

  function projectStem() {
    const name = state.video.fileName ?? state.pgn.headers.White ?? 'project'
    return name.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '-').slice(0, 40) || 'project'
  }

  function refresh() {
    const moves = state.pgn.moves.length
    const tagged = state.stamps.filter((s) => s != null).length
    jsonBtn.disabled = csvBtn.disabled = !moves
    kitBtn.disabled = !tagged || !state.video.width || !state.video.height
    movieBtn.disabled = !activeRender && (!tagged || !state.video.url || !state.video.width)
    clear(status)
    if (!moves) {
      status.append('Load a game to enable exports.')
    } else if (!tagged) {
      status.append(`${moves} moves loaded — tag timestamps to build the render kit.`)
    } else {
      const dims = state.video.width ? `${state.video.width}×${state.video.height}` : 'video not attached'
      status.append(`${tagged} of ${moves} moves tagged · ${dims}`)
    }
  }

  function exportJson() {
    const snapshot = buildSnapshot(state)
    downloadBlob(
      `pgn-sync-${projectStem()}.json`,
      new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' }),
    )
    toast('Project JSON downloaded — import it any time to continue')
  }

  function exportCsv() {
    downloadBlob(
      `pgn-sync-${projectStem()}.csv`,
      new Blob([buildCsv(state.pgn.moves, state.stamps)], { type: 'text/csv' }),
    )
  }

  // Board canvases per segment — shared by the in-browser render (drawn
  // directly) and the ffmpeg kit (converted to PNGs).
  async function buildBoardCanvases(segments, sizePx) {
    const theme = BOARD_THEMES[state.overlay.theme] ?? Object.values(BOARD_THEMES)[0]
    const spriteFile = PIECE_SETS[state.overlay.pieces]?.file ?? 'standard.svg'
    const pieceImages = await loadPieceImages(`${CM_ASSETS_URL}pieces/${spriteFile}`)
    const boards = []
    for (const seg of segments) {
      const move = seg.index >= 0 ? state.pgn.moves[seg.index] : null
      boards.push({
        seg,
        canvas: await renderBoardCanvas({
          fen: move ? move.after : state.pgn.startFen,
          sizePx,
          theme,
          pieceImages,
          lastMove: move,
          orientation: state.overlay.orientation,
          coords: state.overlay.coords,
          opacity: state.overlay.opacity,
        }),
      })
    }
    return boards
  }

  let activeRender = null
  async function exportMovie() {
    if (activeRender) {
      activeRender.cancel()
      return
    }
    const { width, height, duration, url } = state.video
    const segments = buildSegments(state.stamps, duration, state.overlay.showBefore)
    if (!segments.length || !url || !width || !height) return
    if (!pickRecorderMime()) {
      toast("This browser can't record video — use the render kit instead", { kind: 'warn' })
      return
    }
    const originalLabel = movieBtn.textContent
    try {
      movieBtn.textContent = 'Preparing boards…'
      const splitMode = state.overlay.mode === 'split'
      const sizePx = splitMode
        ? splitGeometry({
            share: state.overlay.split.share,
            videoW: width,
            videoH: height,
            pos: state.overlay.split.pos,
          }).boardPx
        : exportGeometry({ overlay: state.overlay, videoWidth: width, videoHeight: height }).size
      const boards = await buildBoardCanvases(segments, sizePx)
      activeRender = renderMovie({
        src: url,
        duration,
        videoW: width,
        videoH: height,
        overlay: state.overlay,
        boards,
        onProgress: (p) => {
          movieBtn.textContent = `Rendering ${Math.round(p * 100)}% — click to cancel`
        },
      })
      const { blob, extension } = await activeRender.promise
      downloadBlob(`${projectStem()}-with-board.${extension}`, blob)
      toast(
        extension === 'mp4'
          ? 'Video rendered — check your downloads'
          : 'Rendered as WebM (this browser can’t record MP4) — check your downloads',
      )
    } catch (err) {
      if (err?.message === 'cancelled') {
        toast('Render cancelled')
      } else {
        console.error('[export] in-browser render failed', err)
        toast(`Render failed: ${err.message}`, { kind: 'warn' })
      }
    } finally {
      activeRender = null
      movieBtn.textContent = originalLabel
      refresh()
    }
  }

  async function exportKit() {
    const { width, height, duration, fileName } = state.video
    const segments = buildSegments(state.stamps, duration, state.overlay.showBefore)
    if (!segments.length || !width || !height) return
    const originalLabel = kitBtn.textContent
    kitBtn.disabled = true
    try {
      kitBtn.textContent = `Rendering ${segments.length} boards…`
      const splitMode = state.overlay.mode === 'split'
      let geometry
      let filterText
      let extra = ''
      if (splitMode) {
        const g = splitGeometry({
          share: state.overlay.split.share,
          videoW: width,
          videoH: height,
          pos: state.overlay.split.pos,
        })
        geometry = { x: g.boardX, y: g.boardY, size: g.boardPx }
        filterText = buildSplitFilterScript(segments, g)
        extra =
          `Layout: split (Shorts) — the output is a fresh ${g.frameW}x${g.frameH} canvas:\n` +
          `  board panel ${g.boardPx}px on top, footage cropped into the ${g.videoBand.w}x${g.videoBand.h} band below.`
      } else {
        geometry = exportGeometry({ overlay: state.overlay, videoWidth: width, videoHeight: height })
        filterText = buildFilterScript(segments, geometry)
      }
      const boards = await buildBoardCanvases(segments, geometry.size)
      const files = []
      for (let i = 0; i < boards.length; i++) {
        files.push({ path: `boards/${segName(i)}`, content: await canvasToPngBlob(boards[i].canvas) })
      }

      const videoFileName = fileName ?? 'input.mp4'
      const { ps1, sh } = buildRenderScripts({ videoFileName, segmentCount: segments.length })
      files.push(
        { path: 'filter.txt', content: filterText },
        { path: 'render.ps1', content: ps1 },
        { path: 'render.sh', content: sh },
        {
          path: 'README.txt',
          content: buildKitReadme({
            videoFileName,
            segmentCount: segments.length,
            geometry,
            videoWidth: width,
            videoHeight: height,
            extra,
          }),
        },
        { path: 'mapping.json', content: JSON.stringify(buildSnapshot(state), null, 2) },
        { path: 'moves.csv', content: buildCsv(state.pgn.moves, state.stamps) },
      )

      kitBtn.textContent = 'Zipping…'
      downloadBlob(`pgn-sync-kit-${projectStem()}.zip`, await buildZip(files))
      toast(`Render kit ready — unzip next to “${videoFileName}” and run the script`)
    } catch (err) {
      console.error('[export] render kit failed', err)
      toast(`Export failed: ${err.message}`, { kind: 'warn' })
    } finally {
      kitBtn.textContent = originalLabel
      refresh()
    }
  }

  async function importProject(file) {
    const { snapshot, error } = parseProjectJson(await file.text())
    if (error) {
      toast(error, { kind: 'warn' })
      return
    }
    if (hasStamps()) {
      const ok = await confirmDialog({
        title: 'Import project?',
        message: 'Importing replaces the current game and all its timestamps.',
        confirmLabel: 'Import',
        danger: true,
      })
      if (!ok) return
    }
    restoreProject(snapshot)
    const needsVideo = snapshot.video?.fileName
    toast(needsVideo ? `Project imported — re-attach “${needsVideo}” to continue` : 'Project imported')
  }

  on('pgn', refresh)
  on('stamps', refresh)
  on('video', refresh)
  refresh()

  parent.appendChild(panel)
  return panel
}

function downloadBlob(name, blob) {
  const url = URL.createObjectURL(blob)
  const a = h('a', { href: url, download: name })
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
